import { Chart, ChartData } from 'chart.js';
import { customFetch as fetch } from './fetch';
import moment from 'moment';
import ko from 'knockout';
import { closePopups } from './init';

interface ChartDataRequestOptions {
    method: string;
    endpoint: string;
    data?: FormData;
}

export enum ChartPeriodRangeSelectorMode {
    FULL_DATE = 1,
    MONTH = 2,
    WEEK = 3
}

export interface ChartPeriodRangeSelectorOptions {
    mode?: ChartPeriodRangeSelectorMode;
}

export class ChartPeriodRangeSelector {
    inYear: KnockoutObservable<number>;
    year: KnockoutObservable<number>;
    month: KnockoutObservable<number>;
    quarter: KnockoutObservable<number>;
    weekNumber: KnockoutObservable<number>;
    savedPeriod: KnockoutObservable<string>;
    dateRange: KnockoutObservable<string>;
    
    lockUpdateSubscriptions: KnockoutObservable<boolean>;

    formattedPeriod: KnockoutComputed<string>;

    options: KnockoutObservable<ChartPeriodRangeSelectorOptions>;

    constructor(options: ChartPeriodRangeSelectorOptions = { mode: ChartPeriodRangeSelectorMode.FULL_DATE }) {
        const currentYear = moment().year();
        const currentMonth = moment().month();

        this.options = ko.observable<ChartPeriodRangeSelectorOptions>(options);

        this.inYear = ko.observable<number>(currentYear);
        this.year = ko.observable<number>(null);
        this.month = ko.observable<number>(currentMonth);
        this.quarter = ko.observable<number>(null);
        this.weekNumber = ko.observable<number>(null);

        this.dateRange = ko.observable<string>();

        this.weekNumber.extend({ rateLimit: 50 });
        this.weekNumber.subscribe(async (newValue) => {
            if (newValue) {
                this.resetPeriod();
                this.weekNumber(newValue);
            }
        });
        
        this.dateRange.extend({ rateLimit: 50 });
        this.dateRange.subscribe(async (newValue) => {
            if (newValue) {
                this.resetPeriod();
                this.dateRange(newValue);
            }
        })
        this.formattedPeriod = ko.pureComputed(() => {
            if (this.year()) {
                return `jaar ${this.year()}`;
            }

            if (this.month() !== null) {
                return `${moment(new Date(this.inYear(), this.month())).format('MMMM')} ${this.inYear()}`;
            }

            if (this.quarter()) {
                return `Kwartaal ${this.quarter()}`;
            }

            if (this.weekNumber()) {
                const startOfWeek = periodToMoment(this).startOf('isoWeek');
                const endOfWeek = moment(startOfWeek).endOf('isoWeek');

                return `Week ${this.weekNumber()} (${startOfWeek.format('D MMM')} t/m ${endOfWeek.format('D MMM')})`
            }

            if (this.dateRange()) {
                const [start, end] = this.getFilterDates();

                return `${start.format('dddd D MMMM')} t/m ${end.format('dddd D MMMM')}`
            }

            return '';
        });

        this.lockUpdateSubscriptions = ko.observable<boolean>();
        this.savedPeriod = ko.observable<string>(this.formattedPeriod());
    }

    updatePeriod() {
        this.savedPeriod(this.formattedPeriod());
    }

    async select(selector: any, event: Event) {
        if (event.type === 'click') {
            const dataset = (<HTMLAnchorElement>event.target).dataset;
            
            this.resetPeriod();

            //this.lockUpdateSubscriptions(true);
            
            if (dataset.month) {
                this.month(parseInt(dataset.month));
            } else if (dataset.quarter) {
                this.quarter(parseInt(dataset.quarter));
            } else if (dataset.year) {
                this.year(parseInt(dataset.year));
            } else {
                this.year(selector.inYear());
            }
            
            //this.lockUpdateSubscriptions(false);
        }
    }

    resetPeriod() {
       // if (!this.lockUpdateSubscriptions()) {
            this.year(null);
            this.month(null);
            this.quarter(null);
            this.weekNumber(null);
            this.dateRange(null);
       // }
    }

    prevYear() {
        const newYear = moment(this.inYear(), 'YYYY').subtract(1, 'y').year();
        this.inYear(newYear);
        this.year(newYear);
    }

    nextYear() {
        const newYear = moment(this.inYear(), 'YYYY').add(1, 'y').year();
        this.inYear(newYear);
        this.year(newYear);
    }

    prevMonth() {
        if(this.month() === 0){
            this.prevYear();
        }
        const newMonth = moment().month(this.month()).subtract(1, 'M').month();
        this.month(newMonth);
    }

    nextMonth() {
        if(this.month() === 11){
            this.nextYear();
        }
        const newMonth = moment().month(this.month()).add(1, 'M').month();
        this.month(newMonth);
    }

    prevWeek() {
        if(this.weekNumber() === 1){
            this.prevYear();
        }
        const newWeek = moment(this.weekNumber(), 'W').subtract(1, 'w').week();
        this.weekNumber(newWeek);
    }

    nextWeek() {
        if(this.weekNumber() === 52){
            this.nextYear();
        }
        const newWeek = moment(this.weekNumber(), 'W').add(1, 'w').week();
        this.weekNumber(newWeek);
    }
    /**
     * Convert the current period object to form data. If a prefix is provided, it is prepended to each field with a
     * . (dot) character. This is useful for e.g. model binding, where the prefix is the name of the argument we expect
     * to bind to. For example, if the controller action expects a complex type named `params` as a parameters, we can
     * provide the string 'params' as the prefix, so that the final form data fields will be named as `params.{field}`
     *
     * @param prefix a string prepended to each field via a . (dot). It defaults to empty.
     */
    toFormData(prefix = '') {
        if (prefix)
            prefix += '.';

        const fd = new FormData();

        fd.append(`${prefix}inYear`, this.inYear().toString());

        if (this.year()) {
            fd.append(`${prefix}year`, this.year().toString());
        }

        if (this.quarter()) {
            fd.append(`${prefix}quarter`, this.quarter().toString());
        }

        if (this.month() !== null) {
            fd.append(`${prefix}month`, (this.month() + 1).toString()); // Moment.js uses a 0th month .NET doesn't
        }

        if (this.weekNumber()) {
            fd.append(`${prefix}weekNumber`, this.weekNumber().toString());
        }

        if (!!this.dateRange()) {
            const [start, end] = this.getFilterDates();
            fd.append(`${prefix}from`, start.format('YYYY-MM-DD HH:mm'));
            fd.append(`${prefix}to`, end.format('YYYY-MM-DD HH:mm'))
        }

        return fd;
    }

    getFilterDates() {
        let startDate = moment();
        let endDate = moment();

        if (this.dateRange() != undefined && this.dateRange().includes(' tot ')) {
            const dates = this.dateRange().split(' tot ');
            startDate = moment(dates[0], 'DD-MM-YYYY');
            endDate = moment(dates[1], 'DD-MM-YYYY');
        }

        return [startDate, endDate];
    }
    
    closePopup() {
        closePopups();
    }
}

export function periodToMoment(period: ChartPeriodRangeSelector): moment.Moment {
    // Use the 1st of June so we are not spanning over years
    const date = moment({ year: period.year() || period.inYear(), month: 5, date: 1 });

    if (period.weekNumber()) {
        date.week(period.weekNumber());
    } else if (period.month()) {
        date.month(period.month())
            .date(1);
    } else {
        date.month(0)
            .date(1);
    }

    return date;
}

export class ChartHelper {
    chartType: string;
    options: ChartDataRequestOptions;

    ctx: CanvasRenderingContext2D;
    chart: Chart;

    constructor(elementId: string, chartType: string, options: ChartDataRequestOptions) {
        this.chartType = chartType;
        this.options = options;

        let canvas = <HTMLCanvasElement>document.getElementById(elementId)!;
        this.ctx = canvas.getContext('2d')!;
    }

    public initialize(): void {
        fetch(this.options.endpoint, {
            method: this.options.method,
            body: this.options.data
        })
            .then((data: any) => {
                if (typeof this.chart === 'undefined' || this.chart === null) {
                    this.chart = new Chart(this.ctx, {
                        type: this.chartType,
                        data: <ChartData>{
                            labels: data.data.labels,
                            datasets: data.data.datasets
                        },
                        options: {
                            maintainAspectRatio: false,
                            scales: {
                                xAxes: [{
                                    gridLines: {
                                        color: 'rgba(0, 0, 0, 0)'
                                    }
                                }],
                                yAxes: [{
                                    gridLines: {
                                        color: 'rgba(0, 0, 0, .05)'
                                    }
                                }]
                            }
                        }
                    });

                    this.chart.update();
                }

                return data;
            });
    }

    async reload(periodRangeData: FormData) {
        const response = await fetch(this.options.endpoint, {
            method: this.options.method,
            body: periodRangeData
        });

        if (response.ok) {
            this.chart.data = <ChartData>{
                labels: response.data.labels,
                datasets: response.data.datasets
            };

            this.chart.update();
        }
    }
}
